<template>
  <b-modal
    :title="modalTitle"
    dialog-class="modal-xl"
    :modelValue="historyModal"
    body-class="pt-0"
    footer-class="d-flex justify-content-end"
    @update:modelValue="historyModal = $event"
  >
    <template #default>
      <LoaderWrapper ref="loader">
        <div class="mt-3">
          <div v-if="historyResponse.items.length === 0" class="text-center text-muted py-4">
            {{ helper.trans('labels.no_history') }}
          </div>
          <div v-else class="overflow-auto">
            <table class="table table-striped border-light">
              <thead>
                <tr>
                  <th class="text-capitalize-first">{{ helper.trans('labels.created_at') }}</th>
                  <th class="text-capitalize-first">{{ helper.trans('labels.initiator') }}</th>
                  <th class="text-capitalize-first">{{ helper.trans('labels.field') }}</th>
                  <th v-if="showFirstDealColumn" class="text-capitalize-first">{{ helper.trans('labels.first_deal') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="group in groupedHistory" :key="group.changeGroupId">
                  <td class="text-nowrap align-top">{{ dateHelper.format(group.changedAt) }}</td>
                  <td class="align-top">
                    <UserNameLabel v-if="group.initiator" :user="group.initiator" />
                    <span v-else class="text-muted">{{ helper.trans('labels.deleted_user') }}</span>
                    <small v-if="group.changes.length > 1" class="text-muted d-block">
                      {{ helper.trans('labels.fields_changed_count', { count: group.changes.length }) }}
                    </small>
                  </td>
                  <td class="align-top">
                    <div class="d-flex flex-column">
                      <div v-for="change in group.changes" :key="change.id" class="mb-1">
                        <small>
                          <span class="text-muted">{{ formatFieldName(change.fieldName) }}</span>:
                          <template v-if="isJsonChange(change)">
                            <pre class="mb-0 mt-1 small text-danger">{{ formatValue(change.fieldName, change.oldValue) }}</pre>
                            <div class="text-muted my-1"><i class="fa fa-arrow-right"></i></div>
                            <pre class="mb-0 small text-success">{{ formatValue(change.fieldName, change.newValue) }}</pre>
                          </template>
                          <template v-else>
                            <span class="text-danger">{{ formatValue(change.fieldName, change.oldValue) }}</span>
                            <i class="fa fa-arrow-right mx-1 text-muted"></i>
                            <span class="text-success">{{ formatValue(change.fieldName, change.newValue) }}</span>
                          </template>
                        </small>
                      </div>
                    </div>
                  </td>
                  <td v-if="showFirstDealColumn" class="align-top text-nowrap">
                    <template v-if="group.firstDealPublicId">
                      <span v-b-tooltip.hover :title="group.firstDealPublicId">{{ group.firstDealPublicId }}</span>
                      <copy-button class="ms-1" :value="group.firstDealPublicId"></copy-button>
                    </template>
                    <span v-else class="text-muted">—</span>
                  </td>
                </tr>
              </tbody>
            </table>

            <b-pagination
              v-if="historyResponse.totalCount > historyResponse.items.length"
              v-model="historyPage"
              :total-rows="historyResponse.totalCount"
              :per-page="historyResponse.limit"
              :limit="20"
              @update:modelValue="loadHistory()"
              prev-text="Back"
              next-text="Next"
              align="start"
              first-number
              hide-goto-end-buttons
              hide-ellipsis
            ></b-pagination>
          </div>
        </div>
      </LoaderWrapper>
    </template>
    <template #footer>
      <button type="button" @click.prevent="historyModal = false" class="btn btn-outline-dark btn-sm text-capitalize-first">
        {{ helper.trans('button.close') }}
      </button>
    </template>
  </b-modal>
</template>

<script lang="ts">
import {computed, defineComponent, PropType, reactive, ref, toRefs} from 'vue'
import {useStore} from 'vuex'
import {BPagination} from 'bootstrap-vue-3'
import LoaderWrapper from '@/components/LoaderWrapper.vue'
import UserNameLabel from '@/components/UserNameLabel.vue'
import CopyButton from '@/components/CopyButton.vue'
import {helper} from '@/service/CommonHelper'
import {dateHelper} from '@/service/DateHelper'
import {api} from '@/service/APIClient'
import PaginatedResponseDTO from '@/models/PaginatedResponseDTO'
import SettingsHistoryItemDTO from '@/models/SettingsHistoryItemDTO'
import StoreSettingsHistoryItemDTO from '@/models/StoreSettingsHistoryItemDTO'
import {useNotification} from '@kyvg/vue3-notification'

const MASKED_VALUE = '***MASKED***'

const CROSS_BORDER_FIELD_LABELS: Record<string, string> = {
  gap: 'labels.crossBorderOccupancyGap',
  automationGap: 'labels.crossBorderAutomationGap',
  source: 'labels.crossBorderRateSource',
  payInRateSource: 'labels.crossBorderPayInRateSource',
  gapAmount: 'labels.crossBorderGapAmount',
  step: 'labels.crossBorderStep',
}

const parseJsonObject = (value: string | null): object | null => {
  if (!value) {
    return null
  }

  try {
    const parsed = JSON.parse(value)
    return parsed !== null && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

export default defineComponent({
  components: {
    LoaderWrapper,
    UserNameLabel,
    CopyButton,
    BPagination,
  },
  props: {
    entityType: {
      type: String as PropType<'merchant' | 'store' | 'storeCrossBorder' | 'provider' | 'trader'>,
      required: true,
    },
  },
  setup(props) {
    const store = useStore()
    const notification = useNotification()
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)

    const state = reactive({
      historyModal: false,
      entityId: null as number | null,
      modalTitle: '',
      historyPage: 1,
      historyResponse: {
        limit: 5,
        offset: 0,
        totalCount: 0,
        items: [],
      } as PaginatedResponseDTO<SettingsHistoryItemDTO | StoreSettingsHistoryItemDTO>,
    })

    const showFirstDealColumn = computed(() => props.entityType === 'store' || props.entityType === 'storeCrossBorder')
    const maskSecrets = computed(() => props.entityType === 'provider')
    const isTrader = computed(() => props.entityType === 'trader')
    const isStoreCrossBorder = computed(() => props.entityType === 'storeCrossBorder')

    const groupedHistory = computed(() => {
      const grouped: Record<string, {
        changeGroupId: string
        changedAt: string
        initiator: SettingsHistoryItemDTO['initiator']
        changes: SettingsHistoryItemDTO[]
        firstDealPublicId: string | null
      }> = {}

      for (const item of state.historyResponse.items) {
        if (!grouped[item.changeGroupId]) {
          grouped[item.changeGroupId] = {
            changeGroupId: item.changeGroupId,
            changedAt: item.changedAt,
            initiator: item.initiator,
            changes: [],
            firstDealPublicId: null,
          }
        }

        grouped[item.changeGroupId].changes.push(item)

        if (showFirstDealColumn.value) {
          const firstDealPublicId = (item as StoreSettingsHistoryItemDTO).firstDealPublicId
          if (firstDealPublicId) {
            grouped[item.changeGroupId].firstDealPublicId = firstDealPublicId
          }
        }
      }

      return Object.values(grouped).sort(
        (a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime(),
      )
    })

    const formatCrossBorderFieldName = (fieldName: string) => {
      const [root, currency, key] = fieldName.split('.')
      if (root !== 'crossBorderSettings') {
        return fieldName
      }
      if (!currency) {
        return helper.trans('labels.crossBorderRates')
      }
      if (!key) {
        return currency
      }

      const labelKey = CROSS_BORDER_FIELD_LABELS[key]
      return `${currency}: ${labelKey ? helper.trans(labelKey) : key}`
    }

    const formatFieldName = (fieldName: string) => {
      if (isStoreCrossBorder.value) {
        return formatCrossBorderFieldName(fieldName)
      }

      if (!isTrader.value) {
        return fieldName
      }

      const key = `trader_history.field.${fieldName}`
      const translated = helper.trans(key)
      return translated !== key ? translated : fieldName
    }

    const formatValue = (fieldName: string, value: string | null) => {
      if (value === null || value === undefined || value === '') {
        return '—'
      }

      if (maskSecrets.value && value === MASKED_VALUE) {
        return helper.trans('labels.value_changed')
      }

      if (isStoreCrossBorder.value) {
        const json = parseJsonObject(value)
        if (json) {
          return JSON.stringify(json, null, 2)
        }

        const key = fieldName.split('.').pop()
        if (key === 'source' || key === 'payInRateSource') {
          return store.getters['rateSources/get'](value)?.name ?? value
        }
      }

      if (isTrader.value) {
        if (fieldName === 'blockReason') {
          if (value === 'null') {
            return '—'
          }
          const key = `block_reason.${value}`
          const translated = helper.trans(key)
          return translated !== key ? translated : value
        }

        if (['isActive', 'isPayOutActive', 'isPayInTrafficBlocked', 'isPayOutTrafficBlocked', 'isBlocked', 'isDeviceSumCheckDisabled', 'isPayOutDisputeReminderToGroupChat', 'isConcurrentDealsLimitEnabled'].includes(fieldName)) {
          return helper.trans(`trader_history.value.${value}`)
        }
      }

      return value
    }

    const isJsonChange = (change: SettingsHistoryItemDTO) =>
      isStoreCrossBorder.value && (parseJsonObject(change.oldValue) !== null || parseJsonObject(change.newValue) !== null)

    const loadHistory = async () => {
      if (!state.entityId) {
        return
      }

      loader.value?.show()
      try {
        state.historyResponse.offset = state.historyResponse.limit * (state.historyPage - 1)

        if (props.entityType === 'merchant') {
          state.historyResponse = await api.merchantSettingsHistory(state.entityId, state.historyResponse)
        } else if (props.entityType === 'store') {
          state.historyResponse = await api.storeSettingsHistory(state.entityId, state.historyResponse)
        } else if (props.entityType === 'storeCrossBorder') {
          state.historyResponse = await api.storeCrossBorderSettingsHistory(state.entityId, state.historyResponse)
        } else if (props.entityType === 'trader') {
          state.historyResponse = await api.traderHistory(state.entityId, state.historyResponse)
        } else {
          state.historyResponse = await api.call.providers.history(state.entityId, state.historyResponse)
        }
      } catch (error: any) {
        if (error?.response?.status === 403) {
          notification.notify({
            type: 'error',
            title: helper.trans('labels.access_denied'),
          })
          state.historyModal = false
        }
      } finally {
        loader.value?.hide()
      }
    }

    const show = async (entityId: number, title: string) => {
      state.entityId = entityId
      state.modalTitle = title
      state.historyPage = 1
      state.historyModal = true

      if (isStoreCrossBorder.value) {
        store.dispatch('rateSources/loadList')
      }

      await loadHistory()
    }

    return {
      ...toRefs(state),
      loader,
      helper,
      dateHelper,
      groupedHistory,
      showFirstDealColumn,
      show,
      loadHistory,
      formatFieldName,
      formatValue,
      isJsonChange,
    }
  },
})
</script>
